import React, { useState } from 'react';
import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { AimOutlined, CloseOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getShowAddDraftOperationModal, showAddDraftOperationModal, showMapView } from '../../store/app-ui.store';
import DraftOperation from '../../Types/DraftOperation';
import useGooglePlaces from '../../hooks/useGooglePlaces';
import { LocationSearchById, LocationSearchByName } from '../Forms/LocationSearch';
import { addDraftOperation, addGroupedDraftOperation, getOptimizedVehicles } from '../../store/operations.store';
import ItemsEditor, { LocationItem } from '../Forms/ItemsEditor';

const { Option } = Select;

type LocationInfo = {
  name: string;
  locationId: string;
  location: any;
  address: string;
  waiting_time: number | null;
  contact_no?: string;
};

const DraftOperationModal = (): JSX.Element => {
  const { options, setQuery, getPlaceDetails } = useGooglePlaces();
  const showModal = useAppSelector(getShowAddDraftOperationModal);
  const dispatch = useAppDispatch();
  const [formRef] = Form.useForm();
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationInfo, setLocation] = useState<LocationInfo | null>(null);
  const [items, setItems] = useState<LocationItem[]>([]);
  const optimizedVehicles = useAppSelector(getOptimizedVehicles);

  const handleOk = () => {
    formRef.submit();
  };

  const handleClose = () => {
    dispatch(showAddDraftOperationModal(false));
  };

  const handleSubmit = (values: DraftOperation) => {
    const payload = { ...values, location: locationInfo!.location, items };
    if (values.plateNumber) {
      dispatch(addGroupedDraftOperation(payload));
    } else {
      dispatch(addDraftOperation(payload));
    }
    formRef.resetFields();
    setItems([]);
    dispatch(showAddDraftOperationModal(false));
  };

  return (
    <Modal visible={showModal} onCancel={handleClose} title="Add Operation" onOk={handleOk}>
      <Form layout="vertical" form={formRef} name="login-form" onFinish={handleSubmit} initialValues={initValues}>
        <Form.Item label="Address :" name="address">
          <Select
            showSearch
            placeholder="Address"
            onSearch={setQuery}
            options={options}
            filterOption={false}
            loading={locationLoading}
            className="w-100"
            onSelect={async (v: any, o: any) => {
              setLocationLoading(true);
              const placeDetails = await getPlaceDetails(v?.toString() || '');
              if (placeDetails?.geometry) {
                const lat = String(placeDetails.geometry.location?.lat());
                const lng = String(placeDetails.geometry.location?.lng());
                formRef.setFieldsValue({
                  address: o.label?.toString(),
                  location: { lat, lng },
                  locationId: 'N/A',
                  name: 'N/A',
                  waiting_time: null,
                  contact_no: '',
                });
                setLocation({
                  locationId: 'N/A',
                  name: 'N/A',
                  location: { lat: Number(lat), lng: Number(lng) },
                  address: o.label?.toString(),
                  waiting_time: null,
                  contact_no: '',
                });
              }
              setLocationLoading(false);
            }}
            allowClear
            clearIcon={<CloseOutlined />}
            onClear={() => {
              formRef.setFieldsValue({
                address: '',
                location: { lat: null, lng: null },
                waiting_time: null,
                contact_no: '',
              });
            }}
          />
        </Form.Item>
        <Row>
          <Col span={20}>
            <Form.Item label="LocationId :" name="locationId">
              <LocationSearchById
                loading={false}
                value={locationInfo?.locationId || ''}
                onChange={(
                  locationId: string,
                  name: string,
                  address: string,
                  location: any,
                  waiting_time?: any,
                  contact_no?: string,
                ) => {
                  setLocation({ locationId, name, location, address, waiting_time, contact_no });
                  formRef.setFieldsValue({
                    locationId,
                    name,
                    address,
                    waiting_time,
                    contact_no,
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={2} offset={2}>
            <Form.Item label="Map">
              <Button
                icon={<AimOutlined />}
                onClick={() =>
                  dispatch(
                    showMapView({
                      show: true,
                      locations: [locationInfo?.location],
                    }),
                  )
                }
                disabled={!locationInfo?.location}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Location Name :" name="name">
          <LocationSearchByName
            loading={false}
            value={locationInfo?.locationId || ''}
            onChange={(
              locationId: string,
              name: string,
              address: string,
              location: any,
              waiting_time?: any,
              contact_no?: string,
            ) => {
              setLocation({ locationId, name, location, address, waiting_time, contact_no });
              formRef.setFieldsValue({
                locationId,
                name,
                address,
                waiting_time,
                contact_no,
              });
            }}
          />
        </Form.Item>
        <Form.Item label="Waiting Time (mins):" name="waiting_time">
          <Input placeholder="Waiting time in minutes" type="number" min={0} />
        </Form.Item>
        <Form.Item label="Postal Code :" name="postalCode">
          <Input placeholder="Postal Code" />
        </Form.Item>
        <Form.Item label="Contact Number:" name="contact_no">
          <Input placeholder="e.g. 94771234567" />
        </Form.Item>
        <Form.Item label="Value :" name="value">
          <Input placeholder="value" />
        </Form.Item>
        <Form.Item label="Items :">
          <ItemsEditor value={items} onChange={setItems} />
        </Form.Item>

        {optimizedVehicles.length !== 0 && (
          <Form.Item label="Vehicle :" name="plateNumber">
            <Select placeholder="Vehicle">
              {optimizedVehicles.map((a) => (
                <Option value={a} key={a}>
                  {a}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const initValues = {
  address: '',
  locationId: '',
  name: '',
  waiting_time: null,
  postalCode: null,
  contact_no: '',
  capacity: 0,
  priority: 0,
  minWindow: null,
  maxWindow: null,
  isStartingRow: false,
  isEndingRow: false,
  value: 0,
} as DraftOperation;

export default DraftOperationModal;
